import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import LastUpdatedBadge from '../../components/common/LastUpdatedBadge';
import TableOfContents from '../../components/common/TableOfContents';
import useSEO from '../../hooks/useSEO';

const LAST_UPDATED = 'March 12, 2026';

export default function CookiePolicyPage() {
  const { t } = useLanguage();
  const contentRef = useRef(null);
  useSEO({
    title: 'Cookie Policy · U-DORM',
    description: 'How U-DORM uses cookies and similar technologies, including essential, functional, analytics, and marketing cookies.',
    image: '/logo.png',
  });
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5DC] to-[#E8E4D9] dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#2F4F4F] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">U</span>
              </div>
              <span className="text-xl font-bold text-[#2F4F4F] dark:text-white">U-DORM</span>
            </Link>
            <div className="flex items-center gap-3">
              <LastUpdatedBadge date={LAST_UPDATED} />
              <LanguageSwitcher variant="minimal" />
              <Link to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-8">
        <TableOfContents contentRef={contentRef} />
        <div ref={contentRef} className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">Cookie Policy</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Last updated: {LAST_UPDATED}</p>

          <div className="prose prose-gray dark:prose-invert max-w-none space-y-6 text-justify hyphens-auto">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">1. What Are Cookies</h2>
              <p className="text-gray-600 dark:text-gray-300">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">2. How We Use Cookies</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">U-DORM uses cookies for the following purposes:</p>
              
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">2.1 Essential Cookies</h3>
              <p className="text-gray-600 dark:text-gray-300">
                These cookies are necessary for the website to function properly. They enable basic functions like page navigation, secure access to your account, and remembering your login status.
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1 mt-2">
                <li>Authentication tokens</li>
                <li>Session management</li>
                <li>Security features</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">2.2 Preference Cookies</h3>
              <p className="text-gray-600 dark:text-gray-300">
                These cookies remember your preferences and settings to provide a personalized experience.
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1 mt-2">
                <li>Language preferences</li>
                <li>Theme settings (dark/light mode)</li>
                <li>Display preferences</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">2.3 Analytics Cookies</h3>
              <p className="text-gray-600 dark:text-gray-300">
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1 mt-2">
                <li>Pages visited</li>
                <li>Time spent on pages</li>
                <li>Error encounters</li>
                <li>Traffic sources</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">2.4 Functional Cookies</h3>
              <p className="text-gray-600 dark:text-gray-300">
                These cookies enable enhanced functionality and personalization, such as remembering choices you make.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">3. Types of Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 dark:border-gray-700 mt-4">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white">Cookie Name</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white">Purpose</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    <tr>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">udorm_token</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">Authentication</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">7 days</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">udorm_language</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">Language preference</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">udorm_theme</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">Theme preference</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">udorm_cookie_consent</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">Cookie consent status</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">4. Third-Party Cookies</h2>
              <p className="text-gray-600 dark:text-gray-300">
                We may use third-party services that set their own cookies. These include:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
                <li><strong>PayPal:</strong> For payment processing (when making subscription payments)</li>
                <li><strong>Google Sign-In:</strong> For social authentication</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">5. Managing Cookies</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                You can control and manage cookies in several ways:
              </p>
              
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">5.1 Browser Settings</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Most browsers allow you to refuse cookies or delete existing cookies. Here's how to manage cookies in popular browsers:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-1 mt-2">
                <li><strong>Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies</li>
                <li><strong>Firefox:</strong> Options &gt; Privacy & Security &gt; Cookies</li>
                <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies</li>
                <li><strong>Edge:</strong> Settings &gt; Cookies and site permissions</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">5.2 Cookie Consent Banner</h3>
              <p className="text-gray-600 dark:text-gray-300">
                When you first visit our website, you will see a cookie consent banner. You can choose to accept all cookies or customize your preferences.
              </p>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">5.3 Withdraw Consent</h3>
              <p className="text-gray-600 dark:text-gray-300">
                You can withdraw your cookie consent at any time by clearing your browser cookies or using the cookie settings in your browser.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">6. Impact of Disabling Cookies</h2>
              <p className="text-gray-600 dark:text-gray-300">
                If you disable certain cookies, some features of our website may not function properly:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>You may need to log in every time you visit</li>
                <li>Your preferences may not be saved</li>
                <li>Some features may become unavailable</li>
                <li>The website experience may be degraded</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">7. Updates to This Policy</h2>
              <p className="text-gray-600 dark:text-gray-300">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our data practices. When we make changes, we will update the "Last updated" date at the top of this page.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">8. Contact Us</h2>
              <p className="text-gray-600 dark:text-gray-300">
                If you have questions about our use of cookies, please contact us:
              </p>
              <ul className="list-none pl-0 text-gray-600 dark:text-gray-300 mt-4">
                <li><strong>Email:</strong> <a href="mailto:info@mindfulux.org" className="text-[#C85A42] hover:underline">info@mindfulux.org</a></li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2F4F4F] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400">&copy; {new Date().getFullYear()} U-DORM. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/cookie-policy" className="text-gray-400 hover:text-white transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
